import { createElement, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { config } from '../../../../../config';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { reportErrorToSentry } from '../../../../../configuration/setup/sentry';
import {
    useAddAssetTagMutation,
    useCreateTagMutation,
    useDeleteAssetMutation,
    useGetAssetAssociationsQuery,
    useGetAssociationsQuery,
    useGetTagsQuery,
    useRemoveAssetTagMutation,
    useUpdateAssetMutation,
    useUpdateMasterdataMutation,
    getMasterdataUpdate,
    hasAssetChanges,
} from '../../../../services/assetAdministrationApi';
import { isApiError } from '../../../../services/baseQuery';
import { getAssetAssociatedDeviceMap } from '../../../reducers/assets/assetsSliceUtils';
import {
    closableSuccessFormattedNotification,
    closableSuccessNotification,
    nonVanishingFormattedErrorNotification,
} from '../../../components/assets/ClickableNotifications';
import {
    assetDeleteDialogClose,
    assetDeleteDialogOpen,
    changeAssetDetailsTab,
    getAssetDetailTab,
    getDisplayDeleteDialog,
} from '../../../reducers/assets/assetsSlice';
import { AssetsListDetail } from '../../../components/assets/details/AssetsListDetail';
import { SelectedTagStatus, type SelectedTag } from '../../../components/assets/details/types';
import type { Asset, AssociatedDevice, Masterdata, Tag } from '../../../components/assets/types';
import type {
    AssetListDetailOwnProperties,
    AssetListDetailPropertiesFromState,
    AssetListDetailPropsFromDispatch,
    AssetsListDetailTab,
} from './types';

export type AssetListDetailProperties = AssetListDetailPropertiesFromState &
    AssetListDetailPropsFromDispatch &
    AssetListDetailOwnProperties;

const sleep = (milliseconds: number) => new Promise<void>(resolve => window.setTimeout(resolve, milliseconds));

const AssetsListDetailContainer = (props: AssetListDetailOwnProperties) => {
    const dispatch = useAppDispatch();
    const currentAssetDetailsTab = useAppSelector(getAssetDetailTab);
    const displayDeleteDialog = useAppSelector(getDisplayDeleteDialog);
    const { data: allAssociations = [] } = useGetAssociationsQuery();
    const { data: selectedAssociations = [] } = useGetAssetAssociationsQuery(props.asset.id);
    const { data: availableTags = [] } = useGetTagsQuery(props.asset.account_id);
    const [saveAsset, saveAssetResult] = useUpdateAssetMutation();
    const [removeAsset, removeAssetResult] = useDeleteAssetMutation();
    const [saveMasterdata, saveMasterdataResult] = useUpdateMasterdataMutation();
    const [createTag] = useCreateTagMutation();
    const [addAssetTag, addAssetTagResult] = useAddAssetTagMutation();
    const [removeAssetTag, removeAssetTagResult] = useRemoveAssetTagMutation();

    useEffect(() => {
        const error = saveAssetResult.error ?? saveMasterdataResult.error;
        if (!error) {
            return;
        }

        const errorCode = isApiError(error) ? error.code : null;
        const messageId =
            errorCode === 'A17'
                ? 'intl-msg:update.error.A17'
                : errorCode
                  ? `intl-msg:create.error.${errorCode}`
                  : 'intl-msg:update.failure';
        nonVanishingFormattedErrorNotification(messageId, errorCode ?? '');
    }, [saveAssetResult.error, saveMasterdataResult.error]);

    const associatedDevices = useMemo(() => {
        const allDevices = getAssetAssociatedDeviceMap(allAssociations);
        return { ...allDevices, [props.asset.id]: selectedAssociations };
    }, [allAssociations, props.asset.id, selectedAssociations]);

    const addTagWithRetry = async (assetId: string, tagId: string) => {
        for (let retryCount = 0; retryCount < config.actions.assets.maximumCreateTagRetries; retryCount++) {
            try {
                await addAssetTag({ assetId, tagId }).unwrap();
                return;
            } catch {
                if (retryCount === config.actions.assets.maximumCreateTagRetries - 1) {
                    reportErrorToSentry(
                        new Error(
                            `Could not add tag ${tagId} to asset ${assetId} after ${config.actions.assets.maximumCreateTagRetries} retries`
                        )
                    );
                    throw new Error(`Could not add tag ${tagId} to asset ${assetId}`);
                }
                await sleep(config.actions.assets.sleepBetweenCreateTagRetriesMs);
            }
        }
    };

    const actionProps: AssetListDetailPropsFromDispatch = {
        fetchSelectedDevices: async (_assetId: string, _devices: { [key: string]: AssociatedDevice[] }) => {},
        updateAssetTags: async (asset: Asset, tags: SelectedTag[]) => {
            for (const tag of tags) {
                if (tag.status === SelectedTagStatus.removed) {
                    await removeAssetTag({ assetId: asset.id, tagId: tag.id }).unwrap();
                    closableSuccessNotification(
                        createElement(FormattedMessage, {
                            id: 'intl-msg:asset.tag.remove.success',
                            values: { tagName: tag.name },
                        }),
                        'success-notification-tag-removed'
                    );
                }
                if (tag.status === SelectedTagStatus.added || tag.status === SelectedTagStatus.created) {
                    await addTagWithRetry(asset.id, tag.id);
                    closableSuccessNotification(
                        createElement(FormattedMessage, {
                            id: 'intl-msg:asset.tag.add.success',
                            values: { tagName: tag.name },
                        }),
                        'success-notification-tag-added'
                    );
                }
            }
        },
        createAssetTags: async (tags: SelectedTag[], asset?: Asset) => {
            for (const tag of tags) {
                const createdTag: Tag = {
                    id: tag.id,
                    type: tag.type,
                    name: tag.name,
                    account_id: tag.account_id,
                };
                await createTag(createdTag).unwrap();
                if (asset) {
                    await addTagWithRetry(asset.id, tag.id);
                    closableSuccessNotification(
                        createElement(FormattedMessage, {
                            id: 'intl-msg:asset.tag.add.success',
                            values: { tagName: tag.name },
                        }),
                        'success-notification-tag-added'
                    );
                }
            }
        },
        updateAsset: async (_originalAsset: Asset, updatedAsset: Asset) => {
            if (!hasAssetChanges(_originalAsset, updatedAsset)) {
                return;
            }
            await saveAsset(updatedAsset).unwrap();
            closableSuccessFormattedNotification('intl-msg:update.success');
        },
        updateMasterdata: async (_asset: Asset, _original: Masterdata | null, masterdata: Masterdata) => {
            const masterdataUpdate = getMasterdataUpdate(_original, masterdata);
            if (Object.keys(masterdataUpdate).length === 0) {
                return;
            }
            await saveMasterdata({
                assetId: props.asset.id,
                masterdata: masterdataUpdate,
            }).unwrap();
            closableSuccessFormattedNotification('intl-msg:update.success');
        },
        openDeleteDialog: () => dispatch(assetDeleteDialogOpen()),
        closeDeleteDialog: () => dispatch(assetDeleteDialogClose()),
        deleteAsset: async (asset: Asset) => {
            await removeAsset(asset.id).unwrap();
        },
        changeAssetDetailsTab: (tab: AssetsListDetailTab) => dispatch(changeAssetDetailsTab(tab)),
        resetAssetUpdateState: () => {},
        resetAssetDeletionState: () => removeAssetResult.reset(),
    };

    const stateProps: AssetListDetailPropertiesFromState = {
        associatedDevices,
        assetUpdating: saveAssetResult.isLoading || saveMasterdataResult.isLoading,
        assetDeleting: removeAssetResult.isLoading,
        displayDeleteDialog,
        deletionSuccessful: removeAssetResult.isSuccess,
        deletionErrorMessageCode: isApiError(removeAssetResult.error)
            ? (removeAssetResult.error.code ?? 'DEFAULT')
            : null,
        availableTags,
        assetTagsUpdating: addAssetTagResult.isLoading || removeAssetTagResult.isLoading,
        currentAssetDetailsTab,
    };

    return createElement(AssetsListDetail, { ...props, ...stateProps, ...actionProps });
};

export default AssetsListDetailContainer;
